import { pathToURL, ViewingService } from "../../net/Xhr";
import { logger } from "../../../logger/Logger";
import { initPlacement, transformAnimations, transformCameraData } from "../common/SvfPlacementUtils";
import { bin16ToPackedString, unpackHexString } from './HashStrings';
import { LmvVector3 } from '../../../wgs/scene/LmvVector3';
import { MeshFlags } from '../../../wgs/scene/MeshFlags';
import { LmvMatrix4 } from '../../../wgs/scene/LmvMatrix4';
import { allocateUintArray } from "../../../wgs/scene/IntArray";
import { USE_UNORM16_GEOMETRY_QUANTIZATION } from "../../../wgs/globals";
import { ProgressiveReadContext } from "./ProgressiveReadContext";
import { PackFileReader } from '../common/PackReader';
import { readViewpointDefinition } from '../common/Viewpoints';
import { readOverrideSet } from '../common/OverrideSets';
import { readNamedItemTree } from '../common/NamedItemTree';

export const FLUENT_URN_PREFIX = "urn:adsk.fluent:";
export const DS_OTG_CDN_PREFIX = "$otg_cdn$";


export function OtgPackage() {

  // Mark svf as Oscar-file. (which uses sharable materials and geometry)
  this.isOTG = true;

  this.materials = null; //The materials json as it came from the SVF

  this.fragments = null; //will be wrapped in a FragmentList

  this.geompacks = [];

  this.propertydb = {
    attrs: [],
    avs: [],
    ids: [],
    values: [],
    offsets: [],

    // Table to map between Svf and Otg ids.
    // Note that the same db might be reused by a 2D sheet later. So, it's important to include dbid
    // here as well, even if we don't use it for 3D.
    dbid: []
  };

  this.bbox = null; //Overall scene bounds

  this.animations = null; // animations json

  this.globalOffset = { x: 0, y: 0, z: 0 };

  this.pendingRequests = 0;
  this.viewpointProgress = 0;

  this.abortController = new AbortController();

  this.viewpoints = [];
  this.overrideSets = [];
  this.extraMatHashes = {};

  this.extraFragInfo = null;

  this.metadataPromise = null;
  this.materialHashlistPromise = null;

  this.hashlistProgressResolve = null;
}

/**
 * @param {number} materialIndex The index of the material
 * @returns {null|string} Returns the hash or null if invalid index, or the hashlist hasn't been loaded
 */
OtgPackage.prototype.getMaterialHash = function (materialIndex) {

  if (!this.materialHashes?.hashes)
  return null;
  var matHash = this.materialHashes.hashes[materialIndex];

  if (!matHash?.length) return null;

  return matHash;
};

/**
 * Generate material hashes for materials in override set.
 * Get the material indexes from overrideSet and
 * check if they are not already loaded (i.e not added via fragments).
 */
OtgPackage.prototype.getExtraMaterialHashes = async function () {

  let matIndexSet = new Set();
  this.overrideSets?.forEach((overrideSet) => {
    const { defaultMaterialIndex, materialOverrides } = overrideSet;
    if (defaultMaterialIndex)
    matIndexSet.add(defaultMaterialIndex);

    materialOverrides?.forEach((matArrayItem) => {
      // matArrayItem = {dbId:'' , materialIndex: ''}
      matIndexSet.add(matArrayItem.materialIndex); // Get the values for materialIndex only
    });
  });

  // Loading of viewpoint runs asynchronously, so we need to wait for the material hashes to be initialized
  // before we can access them below.
  await this.materialHashlistPromise;

  if (matIndexSet.size > 0) {
    this.needExtraMaterials = true;
  }

  // get the hash string that points to the material
  for (const extraMaterialIndex of matIndexSet) {
    const matHash = this.getMaterialHash(extraMaterialIndex);
    if (matHash) this.extraMatHashes[extraMaterialIndex] = matHash;
  }
};

/**
 * @param {number} geomIndex Geometry index
 * @returns {null|string} Returns the hash or null if invalid index, or the hashlist hasn't been loaded
 */
OtgPackage.prototype.getGeometryHash = function (geomIndex) {

  if (!this.geomMetadata?.hashes) return null;

  var gHash = this.geomMetadata.hashes[geomIndex];

  if (!gHash?.length) return null;

  return gHash;
};

/**
 * If the fragment's transform scale exceeds the unit-aware threshold (_unorm16ScaleThreshold),
 * remap its geomDataIndex to a new highp geometry ID so that UNORM16 quantization is skipped.
 * Must be called after transforms and geomDataIndexes are written.
 *
 * @param {number} fragId
 */
OtgPackage.prototype.remapToHighpIfNeeded = function (fragId) {
  if (!USE_UNORM16_GEOMETRY_QUANTIZATION) return;

  const dst = this.fragments.transforms;
  const off = fragId * 12;
  const sx2 = dst[off] * dst[off] + dst[off + 1] * dst[off + 1] + dst[off + 2] * dst[off + 2];
  const sy2 = dst[off + 3] * dst[off + 3] + dst[off + 4] * dst[off + 4] + dst[off + 5] * dst[off + 5];
  const sz2 = dst[off + 6] * dst[off + 6] + dst[off + 7] * dst[off + 7] + dst[off + 8] * dst[off + 8];
  const threshold = this._unorm16ScaleThreshold;
  const threshold2 = threshold * threshold;
  if (sx2 <= threshold2 && sy2 <= threshold2 && sz2 <= threshold2) return;

  const meshid = this.fragments.geomDataIndexes[fragId];

  if (!this._highpGeomIds) {
    this._highpGeomIds = new Map();
  }

  let newId = this._highpGeomIds.get(meshid);
  if (newId === undefined) {
    newId = this.geomMetadata.hashes.length;
    this._highpGeomIds.set(meshid, newId);
    this.geomMetadata.hashes[newId] = this.getGeometryHash(meshid) + '-highp';
  }

  this.fragments.geomDataIndexes[fragId] = newId;
};

/**
 * Upper bound (inclusive) for geometry ids that can be stored in geomDataIndexes.
 * With UNORM16 quantization, remapToHighpIfNeeded() in the worst case appends one extra "-highp" geometry
 * per base geometry, so to avoid the hassles of reallocation, we use the conservative maximum of ~2*num_geoms.
 */
OtgPackage.prototype.getMaxGeomId = function () {
  return this.metadata.stats.num_geoms * (USE_UNORM16_GEOMETRY_QUANTIZATION ? 2 : 1) + 1;
};

//Set up the fragments, materials, and meshes lists
//which get filled progressively as we receive their data
OtgPackage.prototype.initEmptyLists = function (loadContext) {

  var svf = this;

  var frags = svf.fragments = {};
  frags.length = svf.metadata.stats.num_fragments;
  frags.numLoaded = 0;
  frags.boxes = loadContext.fragmentTransformsDouble ? new Float64Array(frags.length * 6) : new Float32Array(frags.length * 6);
  frags.transforms = loadContext.fragmentTransformsDouble ? new Float64Array(frags.length * 12) : new Float32Array(frags.length * 12);
  frags.materials = allocateUintArray(frags.length, svf.metadata.stats.num_materials + 1); // + 1 because of INVALID_MAT_ID
  frags.geomDataIndexes = allocateUintArray(frags.length, svf.getMaxGeomId()); // this is shared with the FragmentList // + 1 because of INVALID_GEOM_ID
  frags.fragId2dbId = new Int32Array(frags.length);
  frags.visibilityFlags = new Uint16Array(frags.length); // this is shared with the FragmentList
  frags.visibilityFlags.fill(MeshFlags.MESH_VISIBLE);
  frags.topoIndexes = null;

  if (loadContext.buildDbId2FragIdMap) {
    // Keys are dbIds, values are fragIds or (if multiple fragIds share the same dbId) arrays of fragIds
    frags.dbId2fragId = [];
  }

  svf.geomMetadata = {
    hashes: null,
    byteStride: 0,
    version: 0,
    numLoaded: 0,
    hashToIndex: new Map()
  };

  svf.materialHashes = {
    hashes: null,
    byteStride: 0,
    version: 0,
    numLoaded: 0,
    initialized: null
  };

  //Shell object to make it compatible with SVF.
  //Not sure which parts of this are really needed,
  //SceneUnit is one.
  svf.materials = {
    "name": "LMVTK Simple Materials",
    "version": "1.0",
    "scene": {
      "SceneUnit": 8215,
      "YIsUp": 0
    },
    materials: {}
  };


};


OtgPackage.prototype.loadAsyncResource = function (loadContext, resourcePath, responseType, callback) {

  //Launch an XHR to load the data from external file
  var svf = this;

  this.pendingRequests++;

  function xhrCB(responseData) {
    svf.pendingRequests--;

    callback(responseData);
  }

  resourcePath = pathToURL(resourcePath, loadContext.basePath);

  ViewingService.getItem(loadContext, resourcePath, xhrCB, loadContext.onFailureCallback,
  { responseType: responseType || "arraybuffer" }
  );

};


OtgPackage.prototype.loadAsyncProgressive = function (loadContext, resourcePath, onData, onDone, onFailure) {

  const url = pathToURL(resourcePath, loadContext.basePath);

  ViewingService.getItem(loadContext, url, onDone, onFailure,
  {
    ondata: onData,
    abortSignal: this.abortController.signal,
    useFetch: true
  }
  );
};


OtgPackage.prototype.loadMetadata = function (loadContext, path, onViewpointDataLoaded) {

  var svf = this;

  this.metadataPromise = new Promise((resolve) => {
    this.loadAsyncResource(loadContext, path, "json", function (data) {

      //For OTG, there is a single JSON for metadata and manifest,
      //and it's the root
      svf.metadata = data;
      svf.manifest = svf.metadata.manifest;

      // Set numGeoms. Note that this must happen before creating the GeometryList.
      svf.numGeoms = svf.metadata.stats.num_geoms;

      svf.processViewPointData(loadContext, onViewpointDataLoaded);

      svf.processMetadata(loadContext);

      svf.initEmptyLists(loadContext);

      var manifest = svf.metadata.manifest;

      //Add the shared property db files to the property db manifest
      //TODO: this is a bit hacky and hardcoded
      var spdb = manifest.shared_assets.pdb;
      for (let p in spdb) {
        if (svf.propertydb[p])
        svf.propertydb[p].push({ path: spdb[p], isShared: true });else
        {


          //Skip this property db file from the list,
          //we don't know how to handle it.
        }}

      var pdb = manifest.assets.pdb;
      for (let p in pdb) {
        if (svf.propertydb[p])
        svf.propertydb[p].push({ path: pdb[p], isShared: false });else
        {


          //Skip this property db file from the list,
          //we don't know how to handle it.
        }}
      //Optional resources

      if (manifest.assets.animations) {
        svf.loadAsyncResource(loadContext, manifest.assets.animations, "json", function (data) {
          svf.animations = data;
          transformAnimations(svf);
        });
      }

      if (manifest.assets.topology) {
        svf.loadAsyncResource(loadContext, path, "json", function (data) {
          svf.topology = data;
        });
      }

      resolve();
    });
  });
  return this.metadataPromise;
};

OtgPackage.prototype.addDbIdReference = function (fragId, dbId) {

  const frags = this.fragments;

  // Elements can be either a single fragId or an array of fragIds
  let fid = frags.dbId2fragId[dbId];
  if (fid === undefined) {
    frags.dbId2fragId[dbId] = fragId;
  } else {
    if (typeof fid === "number") {
      frags.dbId2fragId[dbId] = [fid, fragId];
    } else {
      fid.push(fragId);
    }
  }
};

OtgPackage.prototype.loadFragmentList = function (loadContext, onFragmentLoaded, onAllFragmentsLoaded) {

  var svf = this;

  var _t = new LmvVector3();
  var _s = new LmvVector3();
  var _q = { x: 0, y: 0, z: 0, w: 1 };
  var _m = new LmvMatrix4(true);
  var _zero = { x: 0, y: 0, z: 0 };

  function readOneFragment(ctx, offset, i) {

    //The index is 1-based (due to the record at index 0 being the file header
    //while the runtime FragmentList is a classic 0-based array, so we subtract 1 here.
    i -= 1;

    var idata = ctx.idata();
    var fdata = ctx.fdata();
    offset = offset / 4;

    var frags = svf.fragments;
    var meshid = frags.geomDataIndexes[i] = idata[offset];
    var materialId = frags.materials[i] = idata[offset + 1];
    var dbId = frags.fragId2dbId[i] = idata[offset + 2];
    var flags = idata[offset + 3];

    //check if the fragment's material and geometry hashes are already known
    //If not, then pause fragment processing until they are
    //NOTE: mesh ID and material ID are 1-based indices.
    if (meshid > svf.geomMetadata.numLoaded ||
    materialId > svf.materialHashes.numLoaded) {
      return false;
    }

    // NOTE: Everything below here needs to be aligned with OtgLoader.processFragListChunk

    // Build reverse mapping from dbId to fragId
    if (loadContext.buildDbId2FragIdMap) {
      svf.addDbIdReference(i, dbId);
    }

    //Decode mesh flags (currently, we only support bitvalue 1 to mark hidden-by-default meshes)
    if (flags & 1) {
      frags.visibilityFlags[i] |= MeshFlags.MESH_NOT_FOR_DISPLAY;
      // LMV-6230: Unset MESH_VISIBLE flag in favor of setting MESH_HIDE as Model Browser controls visibility using MESH_VISIBLE
      frags.visibilityFlags[i] &= ~MeshFlags.MESH_VISIBLE;
    }

    var lo = svf.metadata.fragmentTransformsOffset || _zero;

    //Read the fragment transform
    var to = offset + 4;
    _t.set(fdata[to + 0] + lo.x, fdata[to + 1] + lo.y, fdata[to + 2] + lo.z);
    _q.x = fdata[to + 3];
    _q.y = fdata[to + 4];
    _q.z = fdata[to + 5];
    _q.w = fdata[to + 6];
    _s.set(fdata[to + 7], fdata[to + 8], fdata[to + 9]);

    _m.compose(_t, _q, _s);

    if (svf.placementWithOffset) {
      _m.multiplyMatrices(svf.placementWithOffset, _m);
    }

    var e = _m.elements;
    var dst = frags.transforms;
    var off = i * 12;
    dst[off + 0] = e[0];dst[off + 1] = e[1];dst[off + 2] = e[2];
    dst[off + 3] = e[4];dst[off + 4] = e[5];dst[off + 5] = e[6];
    dst[off + 6] = e[8];dst[off + 7] = e[9];dst[off + 8] = e[10];
    dst[off + 9] = e[12];dst[off + 10] = e[13];dst[off + 11] = e[14];

    svf.remapToHighpIfNeeded(i);

    // If we obtained the exact bboxes from BVHWorker already, don't overwrite them.
    if (!frags.dontComputeBoxes) {
      //Estimated bounding box based on known unit box for the mesh, and the fragment's world transform
      // These are just used temporarily for early rendering until we get the exact bboxes are obtained from BVHWorker.
      // The estimated mostly work, but may be too large in some cases where the PCA axes do not match those of the minimum oriented bbox.

      let dstBoxes = frags.boxes;
      let offBoxes = i * 6;

      // This is LmvBox3.applyMatrix4, but inlined and much simplified
      for (let j = 0; j < 3; j++) {
        let max = Math.abs(e[j]); // these three lines are the inner loop unrolled
        max += Math.abs(e[j + 4]); // think '* 0.5' for the unit box, but we do that once at the end
        max += Math.abs(e[j + 8]); // also we accumulate only max, min is -max
        max *= 0.5;
        let trans = e[12 + j];
        dstBoxes[offBoxes + j] = trans - max; // min is -max
        dstBoxes[offBoxes + j + 3] = trans + max;
      }
    }

    frags.numLoaded = i + 1;

    onFragmentLoaded(i);

    return true;
  }

  this.fragmentsCtx = new ProgressiveReadContext(readOneFragment);

  const onData = async (chunk) => {
    if (this.abortController.signal.aborted) {
      return;
    }
    // Fragments have to wait for the metadata (placement transform) to be loaded
    await this.metadataPromise;
    while (!this.fragmentsCtx.onData(chunk)) {
      // need to wait for the hashlists to load further
      await new Promise((resolve) => this.hashlistProgressResolve = resolve);
      chunk = null; // don't need to send the chunk again, just need to re-trigger processing
    }
  };

  const onDone = () => {
    this.fragmentsCtx = null;

    if (this.fragments.numLoaded < this.metadata.stats.num_fragments) {
      logger.warn("Fragments actually loaded fewer than expected.");
    }

    if (this.abortController.signal.aborted) {
      return;
    }

    onAllFragmentsLoaded();
  };

  this.loadAsyncProgressive(loadContext, "fragments.fl", onData, onDone);
};

OtgPackage.prototype.handleHashListRequestFailures = function (loadContext) {

  if (!this.metadata) {
    // Model root not known yet => Retry later when model root is available
    return;
  }

  // If the model is empty, the hash list files don't exist by design.
  // For this case, we suppress the request failure error.
  if (this.geomHashListFailure && this.metadata.stats.num_geoms > 0) {
    loadContext.onFailureCallback.apply(loadContext, this.geomHashListFailure);
  }

  if (this.matHashListFailure && this.metadata.stats.num_materials > 0) {
    loadContext.onFailureCallback.apply(loadContext, this.matHashListFailure);
  }

  this.geomHashListFailure = null;
  this.matHashListFailure = null;
};

OtgPackage.prototype.loadGeometryHashList = function (loadContext) {

  const readOneHash = (ctx, offset, i) => {

    this.initializeGeometryHashList(ctx.byteStride(), ctx.version());

    this.geomMetadata.hashes[i] = bin16ToPackedString(ctx.i16data(), offset / 2);

    this.geomMetadata.numLoaded = i;

    return true;
  };

  this.geometryCtx = new ProgressiveReadContext(readOneHash, 20);

  const onData = async (chunk) => {
    // have to wait for the metadata to be loaded so that the target arrays are initialized
    await this.metadataPromise;
    this.geometryCtx.onData(chunk);
    // unblock fraglist loading
    this.hashlistProgressResolve?.();
    this.hashlistProgressResolve = null;
  };

  // Hash list request error handling is deferred until model root is known
  const onFailure = (...args) => {
    this.geomHashListFailure = args;
    this.handleHashListRequestFailures(loadContext);
  };

  const onDone = () => {
    this.geometryCtx = null;
  };

  this.loadAsyncProgressive(loadContext, "geometry_ptrs.hl", onData, onDone, onFailure);
};

OtgPackage.prototype.initializeGeometryHashList = function (byteStride, version) {
  if (!this.geomMetadata.hashes) {
    this.geomMetadata.hashes = new Array(this.numGeoms + 1);
    this.geomMetadata.byteStride = byteStride;
    this.geomMetadata.version = version;
  }
};

OtgPackage.prototype.loadMaterialHashList = function (loadContext) {
  let _materialHashesInitializedResolve = null;
  this.materialHashlistPromise = new Promise((resolve) => _materialHashesInitializedResolve = resolve);

  const readOneHash = (ctx, offset, i) => {

    if (!this.materialHashes.hashes) {
      this.numMaterials = this.metadata.stats.num_materials;
      this.materialHashes.hashes = new Array(this.numMaterials + 1);
      this.materialHashes.byteStride = ctx.byteStride();
      this.materialHashes.version = ctx.version();
    }

    this.materialHashes.hashes[i] = bin16ToPackedString(ctx.i16data(), offset / 2);

    this.materialHashes.numLoaded = i;

    return true;
  };

  this.materialsCtx = new ProgressiveReadContext(readOneHash, 20);

  const onData = async (chunk) => {
    // have to wait for the metadata to be loaded so that the target arrays are initialized
    await this.metadataPromise;
    this.materialsCtx.onData(chunk);
    // unblock fraglist loading
    this.hashlistProgressResolve?.();
    this.hashlistProgressResolve = null;
  };

  // Hash list request error handling is deferred until model root is known
  const onFailure = (...args) => {
    this.matHashListFailure = args;
    this.handleHashListRequestFailures(loadContext);
  };

  const onDone = () => {
    this.materialsCtx = null;
    _materialHashesInitializedResolve();
  };

  this.loadAsyncProgressive(loadContext, "materials_ptrs.hl", onData, onDone, onFailure);
};

OtgPackage.prototype.processMetadata = function (loadContext) {

  var svf = this;

  var metadata = svf.metadata;

  initPlacement(svf, loadContext);
  var pt = svf.placementWithOffset;

  if (metadata.cameras) {
    svf.cameras = metadata.cameras;

    for (var i = 0; i < svf.cameras.length; i++) {
      var cam = svf.cameras[i];

      cam.position = new LmvVector3(cam.position.x, cam.position.y, cam.position.z);
      cam.target = new LmvVector3(cam.target.x, cam.target.y, cam.target.z);
      cam.up = new LmvVector3(cam.up.x, cam.up.y, cam.up.z);

      if (pt) {
        transformCameraData(cam, pt);
      }
    }
  }

};

OtgPackage.prototype.initializeDbIdFilter = function (objectIds) {
  if (objectIds) {
    this.dbIdFilter = {};
    for (let i = 0, iEnd = objectIds.length; i < iEnd; i++) {
      this.dbIdFilter[objectIds[i]] = 1;
    }
  }
};

OtgPackage.prototype.makeSharedResourcePath = function (cdnUrl, whichType, hash) {

  //TODO: Make this logic preferentially use the CDN paths settings from the
  //viewable manifest instead of the .shared_assets in the per-model settings.
  //In general those will be equal though.

  if (hash.length === 10)
  hash = unpackHexString(hash);

  if (cdnUrl) {

    let shardChars = this.manifest.shared_assets.global_sharding || 0;

    //The shard prefix is a number of character cut off from the hash
    //string and brought to the beginning of the S3 key to improve S3 read
    //preformance.
    let fname = hash;
    let shardPrefix = "";
    if (shardChars) {
      shardPrefix = "/" + hash.slice(0, shardChars);
      fname = hash.slice(shardChars);
    }

    //This prefix is an account ID hash, plus a relative path
    //that is one of /t /g or /m (texture, geometry, material)
    var prefix = this.manifest.shared_assets[whichType];

    if (prefix.indexOf(FLUENT_URN_PREFIX) === 0 || prefix.indexOf(DS_OTG_CDN_PREFIX) === 0) {
      //The CDN bucket name is the first string after the last ":", so skip that too,
      //by slicing up to the first slash
      prefix = prefix.slice(prefix.indexOf("/"));
    } else {
      //it can be a relative path in case of local testing data
      //TODO: not sure this branch will ever get hit
      var split = prefix.split("/");
      prefix = "/" + (split[split.length - 1] || split[split.length - 2]) + "/";
    }

    return cdnUrl + shardPrefix + prefix + fname;

  } else {
    //Locally stored data (testing only) defaults to sharding size of 2 chars
    let shardChars = this.manifest.shared_assets.global_sharding || 2;

    let fname = hash;
    let shardPrefix = "";
    if (shardChars) {
      shardPrefix = "/" + hash.slice(0, shardChars) + "/";
      fname = hash.slice(shardChars);
    }

    var modelBasePath = pathToURL(this.basePath);
    return modelBasePath + this.manifest.shared_assets[whichType] + shardPrefix + fname;
  }
};

OtgPackage.prototype.processViewPointData = function (loadContext, onViewpointDataLoaded) {
  var svf = this;
  const viewpointData = svf.manifest.assets.viewpoints;

  // Store the flag for later use in the loader (hydrate vs replace mode)
  svf.processExtraViewpoints = loadContext.processExtraViewpoints;

  // If no viewpoint data exists, we're done
  if (!viewpointData) {
    onViewpointDataLoaded();
    return;
  }

  if ("camera_definitions" in viewpointData) {
    svf.loadCameraDefinition(loadContext, viewpointData['camera_definitions'], onViewpointDataLoaded);
  }
  if ('override_sets' in viewpointData) {
    svf.loadOverrideSets(loadContext, viewpointData['override_sets'], onViewpointDataLoaded);
  }
  if ("viewpoint_tree" in viewpointData) {
    svf.loadViewpointTree(loadContext, viewpointData['viewpoint_tree'], onViewpointDataLoaded);
  }
  if ("viewpoints" in viewpointData) {
    svf.loadViewpoints(loadContext, viewpointData['viewpoints'], onViewpointDataLoaded);
  }
};

OtgPackage.prototype.postLoadViewpointData = async function (onViewpointDataLoaded) {
  this.viewpointProgress++;

  if (this.viewpointProgress !== 4) {
    return;
  }

  this.processViewpoints();
  await this.getExtraMaterialHashes();
  onViewpointDataLoaded();
};

OtgPackage.prototype.loadCameraDefinition = function (loadContext, path, onViewpointDataLoaded) {
  let svf = this;

  svf.loadAsyncResource(loadContext, path, null, function (data) {
    svf.cameraDef = new PackFileReader(data);
    svf.postLoadViewpointData(onViewpointDataLoaded);
  });
};

OtgPackage.prototype.loadOverrideSets = function (loadContext, path, onViewpointDataLoaded) {
  let svf = this;

  svf.loadAsyncResource(loadContext, path, null, function (data) {
    svf.overrideSetsPack = new PackFileReader(data);
    if (svf.overrideSetsPack) {
      for (let i = 0, iEnd = svf.overrideSetsPack.getEntryCounts(); i < iEnd; i++) {
        const set = readOverrideSet(svf.overrideSetsPack, i);

        if (set) {
          svf.overrideSets.push(set);
        }
      }

      svf.overrideSetsPack = null;
      svf.postLoadViewpointData(onViewpointDataLoaded);
    }
  });
};

OtgPackage.prototype.loadViewpointTree = function (loadContext, path, onViewpointDataLoaded) {
  let svf = this;

  let processViewpointTree = function () {
    const vtr = svf.viewpointTreeRoot;

    if (vtr) {
      vtr.name = vtr.name || 'Saved Viewpoints';
    }

    if (!vtr?.children) {
      return;
    }

    let order = 0;
    function traverseBubble(node) {
      if (node.children) {
        for (let i = 0; i < node.children.length; i++) {
          traverseBubble(node.children[i]);
        }
        node.type = 'folder';
        node.role = 'viewable';
        node.guid = order++ + '';
      } else {
        if (!isNaN(node.entry)) {
          node.order = order;

          node.type = 'view';
          node.role = '3d';
          node.guid = order++ + '';
          node.isViewpoint = true;
        }
      }
    }


    for (let i = 0; i < vtr.children.length; i++) {
      traverseBubble(vtr.children[i]);
    }
  };

  svf.loadAsyncResource(loadContext, path, null, function (data) {
    svf.viewpointTreePack = new PackFileReader(data);
    if (svf.viewpointTreePack) {
      const root = readNamedItemTree(svf.viewpointTreePack);
      svf.viewpointTreeRoot = root;
      processViewpointTree();

      svf.viewpointTreePack = null;
      svf.postLoadViewpointData(onViewpointDataLoaded);
    }
  });
};

OtgPackage.prototype.loadViewpoints = function (loadContext, path, onViewpointDataLoaded) {
  let svf = this;

  svf.loadAsyncResource(loadContext, path, null, function (data) {
    svf.viewpointsDefPack = new PackFileReader(data);
    svf.postLoadViewpointData(onViewpointDataLoaded);
    // Delay processing the data to make sure Camera Def is ready.
  });
};

/**
 * This function is added to make sure both viewpointsDefPack and cameraDef
 * are available.
 */
OtgPackage.prototype.processViewpoints = function () {
  if (this.viewpointsDefPack && this.cameraDef) {
    for (let i = 0, iEnd = this.viewpointsDefPack.getEntryCounts(); i < iEnd; i++) {
      const def = readViewpointDefinition(this.viewpointsDefPack, this.cameraDef, i);

      if (def) {
        this.viewpoints.push(def);
      }
    }

    this.viewpointsDefPack = null;
    this.cameraDef = null;
  }
};

OtgPackage.prototype.abort = function () {
  this.abortController.abort();
};